import React from "react";
import PropTypes from 'prop-types';

import IconContext from "../context/IconContext";

// Define the type of the React component being given via context
type SvgComponentProps = React.SVGProps<SVGSVGElement> 
// Create an interface containing the icons needed for this component
interface IIconContext {
    IconCancel: React.ComponentType<SvgComponentProps>;

    // To use custom SVGs, update the config to define these keys.
    IconOverlayClose: React.ComponentType<SvgComponentProps>;
}

interface OverlayProps extends React.HTMLAttributes<HTMLElement> {
    isHidden?: boolean;
    title?: string;
    // Baseline arguments
    children?: React.ReactNode;
    className?: string;
    // Functions
    onClose?: () => void;
}

/**
 * An eyebrow that usually goes above headers.
 * 
 * ## _
 * ### Arguments
 * @param {boolean} isHidden - Controls the visibility of the overlay. Defaults to true.
 * @param {string} title - The title at the top of the overlay.
 * @param {React.ReactNode} children - The contents inside the eyebrow. This is usually just text.
 * @param {string} className - CSS classes to apply to the eyebrow.
 * 
 * ## _
 * ### Functions
 * @param {function} onClose - Function that is called when the overlay is closed.
 * 
 * ## _
 * ### Icons
 * Declare these in your application's icon configuration.
 * @inner `IconOverlayClose` - An optional icon override for the close icon.
 */
class Overlay extends React.Component<OverlayProps> {
    // Establish the context and tell TypeScript that we're TOTALLY having it in the shape of this thing.
    static contextType = IconContext;
    context!: IIconContext;

    static propTypes = {
        isHidden: PropTypes.bool,
        title: PropTypes.string,
        children: PropTypes.node,
        className: PropTypes.string,
        onClose: PropTypes.func,
    }

    static defaultProps = {
        isHidden: true,
        className: "",
    }

    render() {
        const { isHidden, title, children, className, onClose, ...rest } = this.props;
        const {
            IconCancel,
            IconOverlayClose,
        } = this.context;

        const CloseIcon = IconOverlayClose || IconCancel;

        return (
            <div className={`o-overlay ${!isHidden ? "o-overlay--shown " : ""}${className}`} {...rest}>
                <button className="o-overlay__close-btn" onClick={onClose}>
                    <CloseIcon className="o-overlay__close-icon" />
                </button>
                <div className="o-overlay__container">
                    <h2 className="o-overlay__title">{title}</h2>
                    {children}
                </div>
            </div>
        );
    }
}

export default Overlay;